import { useGetSponsorshipsChangesMutation } from "src/services/sponsorshipsServices";
import {
  LargeHeadingStyled,
  SectionHeaderStyled,
} from "../../../components/custom/texts/texts.styled";
import { Card, Flex, Select } from "@nubeteck/components";
import { Input, Layout, Table, Form, Button, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { IPatrociniosCambio } from "src/Interfaces/sponsorship";
import { useMediaQuery } from "src/hooks";
import { useNavigate } from "react-router-dom";
import { FormatterUtils } from "@nubeteck/utils";
import { formatDate, formatDateTime } from "src/utils/date-util";
import { getSponsorAutoservicePage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";

const { Content } = Layout;

const SponsorshipsChanges: React.FC = () => {
  const [pageNumber, setPageNumber] = React.useState(1);
  const [rowsCount, setRowsCount] = React.useState(5);
  const [studentName, setStudentName] = React.useState("");
  const [sponsorshipName, setSponsorshipName] = React.useState("");
  const [filtroUsado, setFiltroUsado] = React.useState(null);
  const isMobile = useMediaQuery("(max-width: 1180px)");

  const { locale } = useLocale();
  const [sponsorAutoservicePage, setSponsorAutoservicePage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const detail = sponsorAutoservicePage?.sponsor_changes_detail;

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);

    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSponsorAutoservicePage(null);
        setIsLoadingContent(false);
        setHasError(locale === "en");
      }
    }, 3000);

    getSponsorAutoservicePage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSponsorAutoservicePage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSponsorAutoservicePage(null);
          setIsLoadingContent(false);
          setHasError(locale === "en");
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const [getSponsorships, { data, isLoading }] = useGetSponsorshipsChangesMutation();

  const navigate = useNavigate();
  useEffect(() => {
    getSponsorships({
      rowsCount,
      page: pageNumber,
      estudianteNombre: studentName,
      patrocinioNombre: sponsorshipName,
      usado: filtroUsado,
    });
  }, [getSponsorships, pageNumber, rowsCount, studentName, sponsorshipName, filtroUsado]);

  const columns = React.useMemo<ColumnsType<IPatrociniosCambio>>(
    () => [
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[0]?.label || "Usuario",
        dataIndex: "usuario",
        width: 150,
        fixed: isMobile ? undefined : "left",
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[1]?.label || "Fecha modificación",
        dataIndex: "fechaModificacion",
        width: 120,
        fixed: isMobile ? undefined : "left",
        render: (value) => formatDateTime(value),
        sorter: (a, b) =>
          new Date(a.fechaModificacion).getTime() - new Date(b.fechaModificacion).getTime(),
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[2]?.label || "ID",
        dataIndex: "matricula",
        width: 120,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[3]?.label || "Estudiante",
        dataIndex: "estudianteNombre",
        width: 150,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[4]?.label || "Patrocinio",
        dataIndex: "patrocinioNombre",
        width: 150,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[5]?.label || "Tipo de patrocinio",
        dataIndex: "patrocinioTipo",
        width: 150,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[6]?.label || "Carrera",
        dataIndex: "carreraCodigo",
        width: 150,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[7]?.label || "Año",
        dataIndex: "anio",
        width: 80,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[8]?.label || "Periodo",
        dataIndex: "periodo",
        width: 90,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[9]?.label || "No. de acuerdo",
        dataIndex: "acuerdo",
        width: 120,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[10]?.label || "Referencia",
        dataIndex: "referencia",
        width: 150,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[11]?.label || "Monto límite",
        dataIndex: "monto",
        render: (value) => FormatterUtils.number(value.toFixed(2)),
        width: 120,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[12]?.label || "Porcentaje",
        dataIndex: "porcentaje",
        render: (value) => `${value}%`,
        width: 110,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[13]?.label || "Moneda",
        dataIndex: "monedaNombre",
        width: 150,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[14]?.label || "Fecha elegibilidad",
        dataIndex: "fechaElegibilidad",
        width: 120,
        render: (value) => formatDate(value),
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[15]?.label || "Estado",
        dataIndex: "estaActivo",
        render: (value) => (value ? detail?.active_status || "Activo" : detail?.inactive_status || "Inactivo"),
        width: 100,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[16]?.label || "Ha sido utilizado",
        dataIndex: "usado",
        render: (value) => (value ? detail?.yes_label || "Si" : detail?.no_label || "No"),
        width: 100,
      },
      {
        title: sponsorAutoservicePage?.sponsor_changes_table?.[17]?.label || "Monto utilizado",
        dataIndex: "montoUtilizado",
        render: (value) => (value > 0 ? FormatterUtils.number(value) : 0),
        width: 100,
      },
    ],
    [isMobile, sponsorAutoservicePage, detail],
  );
  if (hasError && locale === "en") {
    return (
      <StrapiError
        onRetry={() => {
          setHasError(false);
          setIsLoadingContent(true);
        }}
        onUseDefault={() => {
          setHasError(false);
          setIsLoadingContent(false);
          setSponsorAutoservicePage(null);
        }}
      />
    );
  }

  if (isLoadingContent) {
    return (
      <Flex style={{ width: "100%", flexDirection: "column", gap: 16 }}>
        <Skeleton.Input active size="large" style={{ width: 300 }} />
        <Skeleton active paragraph={{ rows: 6 }} />
      </Flex>
    );
  }

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <SectionHeaderStyled>
        <LargeHeadingStyled>{detail?.title || "Historial de cambios en patrocinios"}</LargeHeadingStyled>
        <Button title="" type="default" onClick={() => navigate("/admin/sponsorships")}>
          {detail?.back_button || "Volver atrás"}
        </Button>
      </SectionHeaderStyled>
      <Content style={{ marginTop: "16px" }}>
        <SectionHeaderStyled style={{ alignContent: "end", gap: "5px" }}>
          <Form.Item label={detail?.search_by_name_placeholder || "Búsqueda por nombre o id"}>
            <Input
              type="text"
              onChange={(e) => setStudentName(e.target.value)}
              placeholder={detail?.search_by_name_placeholder || "Búsqueda por nombre o id"}
            />
          </Form.Item>{" "}
          <Form.Item label={detail?.search_by_sponsorship_placeholder || "Búsqueda por patrocinio"}>
            <Input
              type="text"
              onChange={(e) => setSponsorshipName(e.target.value)}
              placeholder={detail?.search_by_sponsorship_placeholder || "Búsqueda por patrocinio"}
            />
          </Form.Item>
          <Form.Item label={detail?.filter_usage_label || "Filtro por uso"}>
            <Select
              allowClear
              defaultValue={null}
              options={[
                {
                  label: detail?.filter_all_option || "Mostrar todos",
                  value: null,
                },
                {
                  label: detail?.filter_used_option || "Mostrar utilizados",
                  value: true,
                },
                {
                  label: detail?.filter_unused_option || "Mostrar no utilizados",
                  value: false,
                },
              ]}
              placeholder={detail?.filter_placeholder || "Seleccione un  filtro"}
              onChange={(value) => {
                setFiltroUsado(value);
              }}
            />
          </Form.Item>
        </SectionHeaderStyled>

        <Card bodyStyle={{ padding: 0, width: "100%" }}>
          <Table<IPatrociniosCambio>
            loading={isLoading}
            columns={columns}
            dataSource={data?.data?.result}
            rowKey={(v) => `${v.estudiantePatrocinioId} ${v.patrocinioNombre}`}
            pagination={{
              onChange(page, pageSize) {
                setPageNumber(page);
                setRowsCount(pageSize);
              },
              pageSize: rowsCount,
              current: pageNumber,
              total: data?.data?.pageCount * rowsCount,
              showSizeChanger: true,
              style: { marginRight: "30px" },
            }}
            scroll={{ x: 300 }}
          />
        </Card>
      </Content>
    </Flex>
  );
};

export default SponsorshipsChanges;
